/**
 * Formats a date in a consistent way across the application
 * @param date Date object to format
 * @param options Optional configuration for the date formatter
 * @returns Formatted date string
 */
export function formatDate(date: Date, options: Intl.DateTimeFormatOptions = {}): string {
  // Default options for date formatting
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
}

/**
 * Formats a date without time information
 * @param date Date object to format
 * @returns Formatted date string (without time)
 */
export function formatDateOnly(date: Date): string {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: undefined,
    minute: undefined,
  });
}

/**
 * Formats a date from string inputs
 * @param dateStr Date string in YYYY-MM-DD format
 * @param timeStr Optional time string in HH:MM:SS format
 * @returns Formatted date string or '-' if invalid
 */
export function formatDateFromStrings(
  dateStr: string | null,
  timeStr: string | null = null,
): string {
  if (!dateStr) return '-';

  try {
    const dateObj = new Date(dateStr);

    if (Number.isNaN(dateObj.getTime())) {
      return '-'; // Invalid date
    }

    if (timeStr) {
      const [hours, minutes, seconds] = timeStr.split(':').map(Number);
      dateObj.setUTCHours(hours || 0, minutes || 0, seconds || 0);
    }

    return formatDate(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
}
